import { Typography } from '@mui/material'
import React, { type ReactElement, type MouseEvent } from 'react'
import { srLatn } from 'date-fns/locale'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { useTheme } from '@mui/material/styles'
import { ReactComponent as CalendarIcon } from 'assets/calendarIcon.svg'

interface ThemeDatePickerProps {
  label?: string
  isOpen?: boolean
  onClick: (date: Date | null, event?: MouseEvent<HTMLDivElement>) => void
  onClose?: () => void
  showTextField?: boolean
  className?: string
  value: Date | null
  minDate?: Date
  inputWidth?: string
  error?: boolean
}

const weekDays = ['PON', 'UTO', 'SRE', 'ČET', 'PET', 'SUB', 'NED']

const ThemeDatePicker: React.FC<ThemeDatePickerProps> = ({
  showTextField = true,
  minDate,
  onClick,
  onClose,
  className = '',
  isOpen,
  label = '',
  value,
  inputWidth,
  error
}): ReactElement => {
  const { palette, typography } = useTheme()

  class CustomString extends String {
    charAt(_: number): string {
      return this.valueOf()
    }
  }

  const customWeekDays = weekDays.map((day) => new CustomString(day) as string)

  class DateAdapter extends AdapterDateFns {
    getWeekdays = (): string[] => customWeekDays
  }
  return (
    <LocalizationProvider dateAdapter={DateAdapter} adapterLocale={srLatn}>
      <DesktopDatePicker
        open={isOpen}
        value={value}
        onClose={onClose}
        className={className}
        label={
          label && (
            <Typography variant="subtitle1" style={{ lineHeight: '28px' }}>
              {label}
            </Typography>
          )
        }
        onChange={(newDate: Date | null) => {
          if ((newDate && !isNaN(newDate.getTime())) ?? newDate === null) {
            onClick(newDate)
          }
        }}
        format="dd/MM/yyyy"
        minDate={minDate}
        components={{
          OpenPickerIcon: () => <CalendarIcon stroke={palette.indigo300.main} />,
        }}
        slotProps={{
          desktopTrapFocus: {
            disableEnforceFocus: true,
          },
          openPickerButton: {
            sx: {
              width: '32px',
              height: '32px',
            },
          },
          textField: {
            inputProps: {
              sx: {
                paddingBottom: '20px',
                paddingTop: '20px',
              },
            },
            sx: {
              visibility: showTextField ? 'visible' : 'hidden',
              position: showTextField ? 'relative' : 'absolute',
              left: !showTextField ? 0 : '',
              top: !showTextField ? 0 : '',
              '& .MuiInputBase-root': {
                border: error ? '1px solid #D12D55' : 'none',
                width: inputWidth ?? '190px',
                color: palette.blue100.main,
                fontSize: typography.title3.fontSize,
              },
              '& .MuiInputBase-input::placeholder': {
                color: palette.blue100.main,
              },
              '&:hover': {
                '& .MuiInputLabel-root': {
                  color: palette.blue100.main,
                },
              },
              '& .MuiInputLabel-root': {
                '&.Mui-focused': {
                  color: palette.blue100.main,
                },
              },
            },
          },
          popper: {
            sx: {
              '& .MuiPaper-root': {
                boxShadow: !showTextField ? 'none' : '',
              },
            },
          },
          layout: {
            sx: {
              backgroundColor: palette.blue800.main,
              width: '330px',
              '.MuiPickersCalendarHeader-root': {
                marginBottom: '25px',
              },
              '.MuiDayCalendar-weekDayLabel': {
                fontSize: typography.title3.fontSize,
              },
              '& .MuiPickersYear-yearButton': {
                '&:active': {
                  backgroundColor: palette.charts.purple100.main,
                },
                '&.Mui-selected': {
                  backgroundColor: palette.charts.purple100.main,
                },
              },
            },
          },
          previousIconButton: {
            sx: {
              backgroundColor: palette.blue500.main,
              borderRadius: 1,
              height: '20px',
              width: '20px',
              marginRight: '0px',
            },
          },
          leftArrowIcon: {
            sx: {
              height: '18px',
              width: '18px',
            },
          },
          rightArrowIcon: {
            sx: {
              height: '18px',
              width: '18px',
            },
          },
          nextIconButton: {
            sx: {
              backgroundColor: palette.blue500.main,
              borderRadius: 1,
              height: '20px',
              width: '20px',
            },
          },
          day: {
            selected: false,
            onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
              const target: any = e.target
              const timestamp: number = parseInt(target.getAttribute('data-timestamp'), 10)
              const date = new Date(timestamp)
              if (
                value?.getFullYear() === date.getFullYear() &&
                value?.getMonth() === date.getMonth() &&
                value?.getDate() === date.getDate()
              ) {
                onClick(null)
              } else {
                onClick(date)
              }
            },
            sx: {
              fontSize: typography.title5.fontSize,
              '&.MuiPickersDay-dayWithMargin': {
                backgroundColor: 'transparent',
                '&:active': {
                  color: palette.white.main,
                  backgroundColor: palette.charts.purple100.main,
                },
                '&.Mui-selected': {
                  backgroundColor: palette.charts.purple100.main,
                  color: palette.white.main,
                },
              },
            },
          },
        }}
      />
    </LocalizationProvider>
  )
}

export default ThemeDatePicker
